import React, { useEffect, useState } from 'react';
import { Table, Card, Button, Spinner, Form, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import useApi from '../../Api';
import Popup from '../../components/common/popup/Popup';
import '../../styles/pages/Admin/GuideReviewsAdminPage.css';

const GuideReviewsAdminPage = () => {
  const [guideReviews, setGuideReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [approvedFilter, setApprovedFilter] = useState('');
  const [guideIdSearch, setGuideIdSearch] = useState('');
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);
  const [disapproveMessage, setDisapproveMessage] = useState('');
  const [currentReview, setCurrentReview] = useState(null);
  const apiClient = useApi();
  const limit = 10;

  const fetchGuideReviews = async (newApprovedFilter, newGuideIdSearch, reset = false) => {
    if (loading || (!hasMore && !reset)) return;
    setLoading(true);
    try {
      const params = {
        limit,
        cursor: reset ? null : cursor,
      };
      if (newApprovedFilter === 'approved') {
        params.approved = 'approved';
      } else if (newApprovedFilter === 'disapproved') {
        params.approved = 'disapproved';
      } else if (newApprovedFilter === 'needing_review') {
        params.approved = 'needing_review';
      }
      if (newGuideIdSearch.trim() !== '') {
        params.guide_id = newGuideIdSearch.trim();
      }
      const response = await apiClient.get('/admin/guide-reviews', { params });
      const newReviews = response.data.items;
      let filteredReviews;
      if (reset) {
        const seenUnreviewed = {};
        filteredReviews = newReviews.filter(r => {
          if (r.approved === null) {
            if (seenUnreviewed[r.guide_id]) return false;
            seenUnreviewed[r.guide_id] = true;
            return true;
          }
          return true;
        });
      } else {
        const existingUnreviewedGuideIds = new Set(guideReviews.filter(r => r.approved === null).map(r => r.guide_id));
        const seenUnreviewed = {};
        filteredReviews = newReviews.filter(r => {
          if (r.approved === null) {
            if (seenUnreviewed[r.guide_id] || existingUnreviewedGuideIds.has(r.guide_id)) return false;
            seenUnreviewed[r.guide_id] = true;
            return true;
          }
          return true;
        });
      }
      setGuideReviews(prev => (reset ? filteredReviews : [...prev, ...filteredReviews]));
      if (response.data.next_cursor) {
        setCursor(response.data.next_cursor);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching guide reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = async (e) => {
    e.preventDefault();
    setGuideReviews([]);
    setCursor(null);
    setHasMore(true);
    await fetchGuideReviews(approvedFilter, guideIdSearch, true);
  };

  useEffect(() => {
    fetchGuideReviews('', '', true);
  }, []);

  const handleApprove = async (review) => {
    setActionLoading((prev) => ({ ...prev, [review.id]: true }));
    try {
      const response = await apiClient.put(`/admin/guide-reviews/${review.id}`, { approved: true });
      setGuideReviews((prevReviews) =>
        prevReviews.map((gr) => (gr.id === review.id ? response.data : gr))
      );
    } catch (error) {
      console.error('Error approving guide review:', error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [review.id]: false }));
    }
  };

  const openDisapproveModal = (review) => {
    setCurrentReview(review);
    setDisapproveMessage('');
    setShowDisapproveModal(true);
  };

  const closeDisapproveModal = () => {
    setShowDisapproveModal(false);
    setCurrentReview(null);
    setDisapproveMessage('');
  };

  const submitDisapprove = async () => {
    if (!currentReview) return;
    setActionLoading((prev) => ({ ...prev, [currentReview.id]: true }));
    try {
      const response = await apiClient.put(`/admin/guide-reviews/${currentReview.id}`, { approved: false, message: disapproveMessage });
      setGuideReviews((prevReviews) =>
        prevReviews.map((gr) => (gr.id === currentReview.id ? response.data : gr))
      );
      closeDisapproveModal();
    } catch (error) {
      console.error('Error disapproving guide review:', error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [currentReview.id]: false }));
    }
  };

  const handleViewGuide = (guide) => {
    if (guide) {
      window.open(`/guide/${guide.origin_id}/${guide.guide_id}`, '_blank');
    } else {
      alert('Guide has been deleted.');
    }
  };

  const loadMore = () => {
    fetchGuideReviews(approvedFilter, guideIdSearch, false);
  };

  return (
    <>
      <Helmet>
        <title>WeiFinders Admin Guide Reviews</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="guide-reviews-admin-div">
        <Card className="guide-reviews-admin-card">
          <Card.Body>
            <h2 className="guide-reviews-title">Guide Reviews</h2>
            <Form onSubmit={handleFilterSubmit} className="filter-form">
              <Row>
                <Col md={4}>
                  <Form.Group controlId="approvedFilter">
                    <Form.Label>Approval Status</Form.Label>
                    <Form.Control
                      as="select"
                      value={approvedFilter}
                      onChange={(e) => setApprovedFilter(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="approved">Approved</option>
                      <option value="disapproved">Disapproved</option>
                      <option value="needing_review">Needing Review</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="guideIdSearch">
                    <Form.Label>Guide ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Guide ID"
                      value={guideIdSearch}
                      onChange={(e) => setGuideIdSearch(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Button variant="primary" type="submit" className="filter-button">
                    Apply Filters
                  </Button>
                </Col>
              </Row>
            </Form>
            <div className="table-wrapper">
              <Table striped bordered hover responsive className="guide-reviews-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Guide ID</th>
                    <th>Title</th>
                    <th>Reviewer UID</th>
                    <th>Approved</th>
                    <th>Date Submitted</th>
                    <th>Disapproval Message</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {guideReviews.map((gr) => (
                    <tr key={gr.id}>
                      <td>{gr.id}</td>
                      <td>{gr.guide_id}</td>
                      <td>{gr.guide ? gr.guide.title : 'Deleted Guide'}</td>
                      <td>{gr.reviewer_uid || 'N/A'}</td>
                      <td>{gr.approved === null ? 'Needing Review' : gr.approved ? 'Approved' : 'Disapproved'}</td>
                      <td>{new Date(gr.date_submitted).toLocaleString('en-GB')}</td>
                      <td>{gr.disapproval_message || '-'}</td>
                      <td>
                        {gr.approved !== true && (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleApprove(gr)}
                            className="action-button"
                            disabled={actionLoading[gr.id]}
                          >
                            {actionLoading[gr.id] ? <Spinner as="span" animation="border" size="sm" /> : 'Approve'}
                          </Button>
                        )}
                        {gr.approved !== false && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => openDisapproveModal(gr)}
                            className="action-button"
                            disabled={actionLoading[gr.id]}
                          >
                            {actionLoading[gr.id] ? <Spinner as="span" animation="border" size="sm" /> : 'Disapprove'}
                          </Button>
                        )}
                        <Button
                          variant="info"
                          size="sm"
                          onClick={() => handleViewGuide(gr.guide)}
                          className="action-button"
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {loading && (
              <div className="loading-spinner">
                <Spinner animation="border" variant="primary" />
              </div>
            )}
            {!loading && hasMore && (
              <Button variant="primary" onClick={loadMore} className="load-more-button">
                Load More
              </Button>
            )}
            {!loading && !hasMore && (
              <p className="no-more-reviews">No more guide reviews to display.</p>
            )}
          </Card.Body>
        </Card>
        {showDisapproveModal && (
          <Popup
            title="Disapprove Guide"
            onCancel={closeDisapproveModal}
            className="disapprove-popup"
            footer={
              <>
                <Button variant="secondary" onClick={closeDisapproveModal}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={submitDisapprove} disabled={actionLoading[currentReview?.id]}>
                  {actionLoading[currentReview?.id] ? <Spinner as="span" animation="border" size="sm" /> : 'Submit Disapproval'}
                </Button>
              </>
            }
          >
            <Form.Group controlId="disapproveMessage">
              <Form.Control
                as="textarea"
                rows={3}
                value={disapproveMessage}
                onChange={(e) => setDisapproveMessage(e.target.value)}
                placeholder="Enter a message explaining why the guide was disapproved..."
              />
            </Form.Group>
          </Popup>
        )}
      </div>
    </>
  );
};

export default GuideReviewsAdminPage;
